import { Context, Http, handleOperation } from './http'
import { assertValidBucketId } from './validation'
import {
  Result,
  Bucket,
  ListBucketOptions,
  CreateBucketOptions,
  UpdateBucketOptions,
  MessageResponse,
} from './types'

/**
 * Bucket 管理基类。提供 Supabase 同名 Bucket 管理 API：
 * `listBuckets` / `getBucket` / `createBucket` / `updateBucket` / `deleteBucket`。
 * `emptyBucket` 暂不开放（见 §12.2）；也不提供 `headBucket`、Bucket CORS、Bucket ACL 等 COS/S3 风格 Bucket API。
 *
 * 持有 `http`（由运行上下文 `{ config, request }` 构造）与 `throwOnError` 状态，
 * 子类（`app.storage` 命名空间）在此之上提供 `from()` 分流。
 */
export class StorageBucketApi {
  protected http: Http
  protected shouldThrowOnError: boolean

  constructor(context?: Context, shouldThrowOnError = false) {
    this.http = new Http(context as Context)
    this.shouldThrowOnError = shouldThrowOnError
  }

  throwOnError(): this {
    this.shouldThrowOnError = true
    return this
  }

  /**
   * 获取 Bucket 列表（偏移分页 + 扁平排序）。
   *
   * @param options 查询参数：`limit`、`offset`、`sortColumn`、`sortOrder`、`search`。
   */
  async listBuckets(options?: ListBucketOptions): Promise<Result<Bucket[]>> {
    return handleOperation(() => {
      const query: Record<string, any> = {}
      if (options?.limit !== undefined) query.limit = options.limit
      if (options?.offset !== undefined) query.offset = options.offset
      if (options?.sortColumn) query.sortColumn = options.sortColumn
      if (options?.sortOrder) query.sortOrder = options.sortOrder
      if (options?.search) query.search = options.search

      return this.http.send<Bucket[]>({
        method: 'GET',
        path: '/bucket/',
        query,
      })
    }, this.shouldThrowOnError)
  }

  /**
   * 获取指定 Bucket 详情。
   *
   * @param bucketId Bucket ID。
   */
  async getBucket(bucketId: string): Promise<Result<Bucket>> {
    return handleOperation(() => {
      assertValidBucketId(bucketId, 'getBucket')
      return this.http.send<Bucket>({
        method: 'GET',
        path: `/bucket/${bucketId}`,
      })
    }, this.shouldThrowOnError)
  }

  /**
   * 创建 Bucket。
   *
   * @param bucketId Bucket ID，同时作为后端 `id` 与 `name` 传入。
   * @param options 创建参数：`public`、`fileSizeLimit`、`allowedMimeTypes`、`type`。
   * @remarks `public` 默认 `false`。
   */
  async createBucket(bucketId: string, options?: CreateBucketOptions): Promise<Result<{ name: string }>> {
    return handleOperation(() => {
      assertValidBucketId(bucketId, 'createBucket')
      return this.http.send<{ name: string }>({
        method: 'POST',
        path: '/bucket/',
        json: {
          id: bucketId,
          name: bucketId,
          public: options?.public ?? false,
          type: options?.type,
          file_size_limit: options?.fileSizeLimit,
          allowed_mime_types: options?.allowedMimeTypes,
        },
      })
    }, this.shouldThrowOnError)
  }

  /**
   * 更新 Bucket 配置。
   *
   * @param bucketId Bucket ID。
   * @param options 更新参数：`public`、`fileSizeLimit`、`allowedMimeTypes`。不支持更新 `type`。
   */
  async updateBucket(bucketId: string, options: UpdateBucketOptions): Promise<Result<MessageResponse>> {
    return handleOperation(() => {
      assertValidBucketId(bucketId, 'updateBucket')
      return this.http.send<MessageResponse>({
        method: 'PUT',
        path: `/bucket/${bucketId}`,
        json: {
          public: options.public,
          file_size_limit: options.fileSizeLimit,
          allowed_mime_types: options.allowedMimeTypes,
        },
      })
    }, this.shouldThrowOnError)
  }

  /**
   * 删除空 Bucket。
   *
   * @param bucketId Bucket ID。
   * @remarks Bucket 非空时后端会返回错误。
   */
  async deleteBucket(bucketId: string): Promise<Result<MessageResponse>> {
    return handleOperation(() => {
      assertValidBucketId(bucketId, 'deleteBucket')
      return this.http.send<MessageResponse>({
        method: 'DELETE',
        path: `/bucket/${bucketId}`,
      })
    }, this.shouldThrowOnError)
  }
}
